import React, { useState, useEffect, useRef } from 'react';
import { Questions } from './questions';
import { FiCheckCircle, FiShield, FiClock, FiFileText, FiChevronLeft, FiChevronRight, FiX, FiMail } from 'react-icons/fi';

// Logo Component matching shvesawebsite branding
const Logo: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ size = 'md' }) => {
    const sizes = {
        sm: { icon: 'w-6 h-6', text: 'text-lg' },
        md: { icon: 'w-7 h-7', text: 'text-xl' },
        lg: { icon: 'w-8 h-8', text: 'text-2xl' }
    };

    return (
        <div className="flex items-center gap-1">
            <svg className={`${sizes[size].icon} text-teal-600`} viewBox="0 0 24 24" fill="currentColor">
                <path d="M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z"/>
            </svg>
            <span className={`${sizes[size].text} font-bold text-slate-800`}>
                Shvesa <span className="font-light text-teal-600">Minds</span>
            </span>
        </div>
    );
};

interface Option {
    option_text: string;
    option_weight: number;
}

export interface QuestionProps {
    survey_title: string;
    question_text: string;
    question_type: string;
    question_order: number;
    question_weight: number;
    option_type: string;
    question_category: string;
    images: string[];
    options: Option[];
}

export interface AnswersProps {
    [key: number]: string;
}

interface Details {
    age: string,
    name: string;
    email: string;
    country: string;
    phoneNumber: string;
}

const QUESTIONS_PER_PAGE = 10;

const FreeAssessment: React.FC = () => {
    // 🚨 TEST MODE FLAG - SET TO FALSE BEFORE COMMITTING 🚨
    const TEST_MODE = false; // Change to false before production deployment

    const [termsAgreed, setTermsAgreed] = useState(false)
    const [details, setDetails] = useState<Details>({ age: '', name: '', country: '', phoneNumber: '', email: '' });
    const [askDetails, setAskDetails] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [questions, setQuestions] = useState<QuestionProps[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [answers, setAnswers] = useState<AnswersProps>({});
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const questionRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        try {
            setQuestions(Questions || []);

            // 🧪 TEST MODE: Auto-fill answers and details
            if (TEST_MODE) {
                console.log('🧪 TEST MODE ACTIVE - Auto-filling survey');
                const testEmail = 'testuser@shvesa.com';
                setDetails({
                    age: '4',
                    name: 'Test User',
                    country: 'Test Country',
                    phoneNumber: '1234567890',
                    email: testEmail
                });

                const testAnswers: AnswersProps = {};
                const answerOptions = ['Never', 'Rare', 'Sometimes', 'Often'];
                Questions.forEach((question, index) => {
                    if (index < 10) testAnswers[question.question_order] = 'Often';
                    else if (index < 20) testAnswers[question.question_order] = 'Sometimes';
                    else if (index < 30) testAnswers[question.question_order] = 'Rare';
                    else if (index < 40) testAnswers[question.question_order] = 'Never';
                    else testAnswers[question.question_order] = answerOptions[index % 4];
                });
                setAnswers(testAnswers);
            }
        } catch (err) {
            setError('Failed to load questions');
        }
    }, [TEST_MODE]);

    // Track page unload for abandonment detection
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (termsAgreed && !success && Object.keys(answers).length > 0) {
                try {
                    if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
                        (window as any).gtag('event', 'survey_abandoned', {
                            content_group1: 'survey_engagement',
                            questions_answered: Object.keys(answers).length,
                            total_questions: questions.length,
                            current_page: currentPage + 1,
                            completion_rate: Math.round((Object.keys(answers).length / questions.length) * 100)
                        });
                    }
                } catch (error) {
                    console.warn('Survey abandonment tracking failed:', error);
                }
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [termsAgreed, success, answers, questions.length, currentPage]);

    const handleAnswer = (questionIndex: number, question_order: number, optionText: string): void => {
        setAnswers(prev => ({ ...prev, [question_order]: optionText }));

        try {
            if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
                (window as any).gtag('event', 'question_answered', {
                    question_number: question_order,
                    answer: optionText,
                    event_category: 'survey_progress'
                });
            }
        } catch (error) {
            console.warn('Analytics tracking failed:', error);
        }

        const nextQuestionIndex = questionIndex + 1;
        const nextQuestionRef = questionRefs.current[nextQuestionIndex];
        if (nextQuestionRef && nextQuestionIndex < (currentPage + 1) * QUESTIONS_PER_PAGE) {
            setTimeout(() => {
                nextQuestionRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    };

    const getCurrentPageQuestions = (): QuestionProps[] => {
        const startIndex = currentPage * QUESTIONS_PER_PAGE;
        return questions.slice(startIndex, startIndex + QUESTIONS_PER_PAGE);
    };

    const getProgressPercentage = (): number => {
        const totalAnswered = Object.keys(answers).length;
        return questions.length ? (totalAnswered / questions.length) * 100 : 0;
    };

    const handleNextPage = () => {
        if ((currentPage + 1) * QUESTIONS_PER_PAGE < questions.length) {
            setCurrentPage(prev => prev + 1);
            window.scrollTo(0, 0);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 0) {
            setCurrentPage(prev => prev - 1);
            window.scrollTo(0, 0);
        }
    };

    const isPageComplete = (): boolean => {
        const currentQuestions = getCurrentPageQuestions();
        return currentQuestions.every((_, index) => {
            const globalIndex = currentPage * QUESTIONS_PER_PAGE + index;
            return answers[globalIndex + 1] !== undefined;
        });
    };

    const isFinalPage = (): boolean => {
        return (currentPage + 1) * QUESTIONS_PER_PAGE >= questions.length;
    };

    const isValidEmail = (email: string): boolean => {
        const pattern = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
        return pattern.test(email);
    };

    const handleSubmitSurvey = async () => {
        if (!isValidEmail(details.email)) {
            alert('Please enter a valid email address');
            return;
        }

        setLoading(true);
        const surveyData = {
            userDetails: {
                age: details.age,
                name: details.name,
                country: details.country,
                phonenumber: details.phoneNumber,
                email: details.email,
                submittedAt: new Date().toISOString(),
            },
            responses: questions.map(question => ({
                questionId: question.question_order,
                questionText: question.question_text,
                questionType: question.question_type,
                category: question.question_category,
                selectedAnswer: answers[question.question_order],
                selectedWeight: question.options.find(
                    opt => opt.option_text === answers[question.question_order]
                )?.option_weight || 0
            }))
        };

        try {
            const response = await fetch('https://e6w2t1d2f1.execute-api.us-east-1.amazonaws.com/dev/survey', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(surveyData)
            });

            if (!response.ok) throw new Error(`Failed to submit survey: ${response.statusText}`);

            setLoading(false);
            setSuccess(true);

            try {
                if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
                    (window as any).gtag('event', 'survey_completed', {
                        total_questions: questions.length,
                        user_age: details.age
                    });
                }
            } catch (error) {
                console.warn('Survey completion tracking failed:', error);
            }
        } catch (error: any) {
            setLoading(false);
            console.error('Error submitting survey:', error);
            if (error instanceof TypeError && error.message === 'Failed to fetch') {
                setError('Network error. Please check your connection and try again.');
            } else {
                setError(error?.response?.data?.message || error?.message || 'Failed to submit survey');
            }
        }
    };

    const handleStartAssessment = () => {
        if (!details.age) {
            alert("Please select your child's age to continue");
            return;
        }
        setTermsAgreed(true);
        try {
            if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
                (window as any).gtag('event', 'survey_started', {
                    child_age: details.age,
                    total_questions: questions.length
                });
            }
        } catch (error) {
            console.warn('Survey start tracking failed:', error);
        }
    };

    const handleSubmitDetails = () => {
        if (!details.email.trim()) {
            alert('Please enter your email address');
            return;
        }
        if (!isValidEmail(details.email)) {
            alert('Please enter a valid email address');
            return;
        }
        handleSubmitSurvey();
    };

    const currentQuestions = getCurrentPageQuestions();
    const totalPages = Math.ceil(questions.length / QUESTIONS_PER_PAGE);

    // Loading State
    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-teal-600 mx-auto mb-4"></div>
                    <p className="text-slate-600 font-medium">Processing your assessment...</p>
                </div>
            </div>
        );
    }

    // Error State
    if (error) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 max-w-md text-center">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FiX className="w-6 h-6 text-red-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-slate-800 mb-2">Something went wrong</h2>
                    <p className="text-slate-600 mb-6">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-6 py-2.5 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    // Success State
    if (success) {
        return (
            <div className="min-h-screen bg-white">
                {/* Header */}
                <header className="border-b border-slate-200">
                    <div className="max-w-2xl mx-auto px-4 py-4">
                        <Logo size="lg" />
                    </div>
                </header>

                <main className="max-w-2xl mx-auto px-4 py-12">
                    <div className="text-center">
                        <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <FiCheckCircle className="w-8 h-8 text-teal-600" />
                        </div>
                        <h1 className="text-2xl font-semibold text-slate-800 mb-3">Assessment Complete</h1>
                        <p className="text-slate-600 mb-8">
                            Your results have been sent to <span className="font-medium">{details.email}</span>
                        </p>

                        {TEST_MODE && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8 text-left">
                                <p className="text-sm font-medium text-amber-800 mb-2">Test Mode Active</p>
                                <a
                                    href={`/assessmentreport?email=${encodeURIComponent(details.email)}`}
                                    className="text-sm text-teal-600 hover:text-teal-700 underline"
                                >
                                    View Report
                                </a>
                            </div>
                        )}

                        {/* Free Consultation CTA */}
                        <div className="mb-8">
                            <h2 className="font-semibold text-slate-800 mb-2">Speak with a Specialist</h2>
                            <p className="text-sm text-slate-600 mb-4">
                                Have questions about your child's results? Book a free consultation with our team.
                            </p>
                            <a
                                href="https://calendly.com/gearupstudios/quickmeet?text_color=414141"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                Free Online Consultation
                            </a>
                        </div>

                        <div className="border-t border-slate-200 pt-8 mb-8">
                            <h2 className="font-medium text-slate-800 mb-2">Want a comprehensive evaluation?</h2>
                            <p className="text-sm text-slate-600 mb-4">
                                Our Detailed Developmental Analysis provides personalized insights reviewed by certified specialists.
                            </p>
                            <a
                                href={`/advancedsurvey?email=${encodeURIComponent(details.email)}`}
                                className="inline-block px-5 py-2.5 bg-slate-800 text-white font-medium rounded-lg hover:bg-slate-900 transition-colors"
                            >
                                Take Detailed Assessment
                            </a>
                            <p className="text-xs text-slate-500 mt-3">For children 3 years and above</p>
                        </div>

                        <p className="text-sm text-slate-500">
                            Results are typically delivered within 3 working days.
                        </p>
                    </div>
                </main>
            </div>
        );
    }

    // Preview State
    if (showPreview) {
        return (
            <div className="min-h-screen bg-slate-50 py-8 px-4">
                <div className="max-w-2xl mx-auto">
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="px-6 py-5 border-b border-slate-200 bg-slate-50">
                            <h2 className="text-xl font-semibold text-slate-800">Review Your Responses</h2>
                            <p className="text-sm text-slate-600 mt-1">Please verify your answers before submitting</p>
                        </div>

                        <div className="p-6 max-h-[60vh] overflow-y-auto">
                            <div className="space-y-4">
                                {questions.map((question, index) => (
                                    <div key={index} className="py-3 border-b border-slate-100 last:border-0">
                                        <p className="text-sm text-slate-600 mb-1">Question {index + 1}</p>
                                        <p className="font-medium text-slate-800 mb-2">{question.question_text}</p>
                                        <p className="text-teal-600 font-medium">{answers[index + 1] || 'Not answered'}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-between">
                            <button
                                onClick={() => setShowPreview(false)}
                                className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors"
                            >
                                Edit Responses
                            </button>
                            <button
                                onClick={() => { setShowPreview(false); setAskDetails(true); }}
                                className="px-6 py-2.5 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors"
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Landing Page (before assessment starts)
    if (!termsAgreed) {
        return (
            <div className="min-h-screen bg-slate-50">
                {/* TEST MODE Indicator */}
                {TEST_MODE && (
                    <div className="fixed top-4 right-4 z-50 bg-amber-500 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
                        Test Mode
                    </div>
                )}

                {/* Header */}
                <header className="bg-white border-b border-slate-200">
                    <div className="max-w-4xl mx-auto px-4 py-4">
                        <Logo size="lg" />
                    </div>
                </header>

                {/* Main Content */}
                <main className="max-w-4xl mx-auto px-4 py-8">
                    <div className="grid lg:grid-cols-5 gap-8">
                        {/* Left Column - Main Content */}
                        <div className="lg:col-span-3">
                            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 md:p-8">
                                <div className="mb-6">
                                    <span className="inline-block px-3 py-1 bg-teal-50 text-teal-700 text-sm font-medium rounded-full mb-4">
                                        Free Assessment
                                    </span>
                                    <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                                        Child Development Screening
                                    </h1>
                                    <p className="text-slate-600 text-lg">
                                        A comprehensive screening tool to help identify developmental patterns in children aged 2-10 years.
                                    </p>
                                </div>

                                {/* Key Features */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                                        <FiClock className="w-5 h-5 text-teal-600 flex-shrink-0" />
                                        <span className="text-sm text-slate-700">10-15 minutes</span>
                                    </div>
                                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                                        <FiShield className="w-5 h-5 text-teal-600 flex-shrink-0" />
                                        <span className="text-sm text-slate-700">Private & Secure</span>
                                    </div>
                                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                                        <FiFileText className="w-5 h-5 text-teal-600 flex-shrink-0" />
                                        <span className="text-sm text-slate-700">Instant Results</span>
                                    </div>
                                </div>

                                {/* Age Selection */}
                                <div className="mb-6">
                                    <label htmlFor="age-select" className="block text-sm font-medium text-slate-700 mb-2">
                                        Child's Age
                                    </label>
                                    <select
                                        id="age-select"
                                        value={details.age}
                                        onChange={(e) => setDetails({ ...details, age: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-800 bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                                    >
                                        <option value="">Select age</option>
                                        {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(age => (
                                            <option key={age} value={age}>{age} years old</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Start Button */}
                                <button
                                    onClick={handleStartAssessment}
                                    className="w-full py-3.5 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors shadow-sm"
                                >
                                    Begin Assessment
                                </button>

                                {/* TEST MODE Quick Skip */}
                                {TEST_MODE && (
                                    <button
                                        onClick={() => {
                                            setTermsAgreed(true);
                                            setTimeout(() => { setShowPreview(true); window.scrollTo(0, 0); }, 100);
                                        }}
                                        className="w-full mt-3 py-2 bg-amber-100 text-amber-700 font-medium rounded-lg hover:bg-amber-200 transition-colors"
                                    >
                                        Skip to Review (Test)
                                    </button>
                                )}

                                {/* Disclaimer */}
                                <p className="text-xs text-slate-500 mt-4 text-center">
                                    This screening tool provides preliminary insights and is not a substitute for professional clinical evaluation.
                                </p>
                            </div>
                        </div>

                        {/* Right Column - Trust Indicators */}
                        <div className="lg:col-span-2 space-y-4">
                            {/* Methodology */}
                            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                                <h3 className="font-semibold text-slate-800 mb-3">Evidence-Based Methodology</h3>
                                <p className="text-sm text-slate-600 mb-4">
                                    Our assessment is developed based on established clinical frameworks used by healthcare professionals.
                                </p>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 text-sm text-slate-600">
                                        <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
                                        <span>NHS guidelines aligned</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-slate-600">
                                        <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
                                        <span>ADOS-2 framework informed</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-slate-600">
                                        <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
                                        <span>CAMHS standards referenced</span>
                                    </div>
                                </div>
                            </div>

                            {/* What to Expect */}
                            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                                <h3 className="font-semibold text-slate-800 mb-3">What to Expect</h3>
                                <ul className="space-y-3 text-sm text-slate-600">
                                    <li className="flex gap-3">
                                        <span className="text-teal-600 font-medium">1.</span>
                                        <span>Answer questions about your child's behavior and development</span>
                                    </li>
                                    <li className="flex gap-3">
                                        <span className="text-teal-600 font-medium">2.</span>
                                        <span>Review your responses before submission</span>
                                    </li>
                                    <li className="flex gap-3">
                                        <span className="text-teal-600 font-medium">3.</span>
                                        <span>Receive a detailed report via email</span>
                                    </li>
                                </ul>
                            </div>

                            {/* Privacy Notice */}
                            <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
                                <div className="flex items-start gap-3">
                                    <FiShield className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <h4 className="font-medium text-slate-700 text-sm">Your Privacy Matters</h4>
                                        <p className="text-xs text-slate-500 mt-1">
                                            All information is encrypted and handled in accordance with data protection regulations.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    // Questions Page
    return (
        <div className="min-h-screen bg-slate-50">
            {/* TEST MODE Indicator */}
            {TEST_MODE && (
                <div className="fixed top-4 right-4 z-50 bg-amber-500 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
                    Test Mode
                </div>
            )}

            {/* Header with Progress */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
                <div className="max-w-3xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between mb-3">
                        <Logo size="md" />
                        <span className="text-sm text-slate-500">
                            Page {currentPage + 1} of {totalPages}
                        </span>
                    </div>
                    {/* Progress Bar */}
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-teal-500 rounded-full transition-all duration-300"
                            style={{ width: `${getProgressPercentage()}%` }}
                        />
                    </div>
                </div>
            </header>

            {/* TEST MODE Controls */}
            {TEST_MODE && (
                <div className="max-w-3xl mx-auto px-4 mt-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
                        <span className="text-sm text-amber-700">All answers pre-filled</span>
                        <button
                            onClick={() => { setShowPreview(true); window.scrollTo(0, 0); }}
                            className="text-sm bg-amber-500 text-white px-3 py-1 rounded font-medium hover:bg-amber-600"
                        >
                            Skip to Review
                        </button>
                    </div>
                </div>
            )}

            {/* Questions */}
            <main className="max-w-3xl mx-auto px-4 py-6">
                <div className="space-y-6">
                    {currentQuestions.map((question, pageIndex) => {
                        const globalIndex = currentPage * QUESTIONS_PER_PAGE + pageIndex;
                        const isAnswered = answers[globalIndex + 1] !== undefined;

                        return (
                            <div
                                key={globalIndex}
                                ref={el => questionRefs.current[globalIndex] = el}
                                className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 md:p-6"
                            >
                                <div className="flex items-start gap-3 mb-4">
                                    <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium
                                        ${isAnswered ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>
                                        {globalIndex + 1}
                                    </span>
                                    <h2 className="text-base md:text-lg font-medium text-slate-800 pt-0.5">
                                        {question.question_text}
                                    </h2>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 ml-10">
                                    {question.options.map((option, optionIndex) => {
                                        const isSelected = answers[globalIndex + 1] === option.option_text;
                                        return (
                                            <button
                                                key={optionIndex}
                                                onClick={() => handleAnswer(globalIndex, question.question_order, option.option_text)}
                                                className={`py-2.5 px-4 rounded-lg border-2 text-sm font-medium transition-all
                                                    ${isSelected
                                                        ? 'border-teal-500 bg-teal-50 text-teal-700'
                                                        : 'border-slate-200 hover:border-slate-300 text-slate-600 hover:bg-slate-50'
                                                    }`}
                                            >
                                                {option.option_text}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Navigation */}
                <div className="flex justify-between items-center mt-8 pb-8">
                    <button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 0}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors
                            ${currentPage === 0
                                ? 'text-slate-300 cursor-not-allowed'
                                : 'text-slate-600 hover:bg-slate-100'
                            }`}
                    >
                        <FiChevronLeft className="w-4 h-4" />
                        Previous
                    </button>

                    {isFinalPage() ? (
                        <button
                            disabled={!isPageComplete()}
                            onClick={() => { setShowPreview(true); window.scrollTo(0, 0); }}
                            className={`px-6 py-2.5 rounded-lg font-medium transition-colors
                                ${isPageComplete()
                                    ? 'bg-teal-600 text-white hover:bg-teal-700'
                                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                }`}
                        >
                            Review Answers
                        </button>
                    ) : (
                        <button
                            onClick={handleNextPage}
                            disabled={!isPageComplete()}
                            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-colors
                                ${isPageComplete()
                                    ? 'bg-teal-600 text-white hover:bg-teal-700'
                                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                }`}
                        >
                            Next
                            <FiChevronRight className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </main>

            {/* Details Modal */}
            {askDetails && (
                <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="px-6 py-5 border-b border-slate-200">
                            <h2 className="text-lg font-semibold text-slate-800">Where should we send your results?</h2>
                        </div>

                        <div className="p-6">
                            <div className="flex items-start gap-3 p-4 bg-teal-50 rounded-lg mb-6">
                                <FiMail className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-teal-800">
                                    Your detailed assessment report will be emailed to you immediately after submission.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Email Address <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        placeholder="your@email.com"
                                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                        value={details.email}
                                        onChange={(e) => setDetails({ ...details, email: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Your Name <span className="text-slate-400">(optional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="First name"
                                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                        value={details.name}
                                        onChange={(e) => setDetails({ ...details, name: e.target.value })}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                            Country <span className="text-slate-400">(optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Country"
                                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                            value={details.country}
                                            onChange={(e) => setDetails({ ...details, country: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                            Phone <span className="text-slate-400">(optional)</span>
                                        </label>
                                        <input
                                            type="tel"
                                            placeholder="Phone number"
                                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                            value={details.phoneNumber}
                                            onChange={(e) => setDetails({ ...details, phoneNumber: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-between">
                            <button
                                onClick={() => { setAskDetails(false); setShowPreview(true); }}
                                className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors"
                            >
                                Back
                            </button>
                            <button
                                onClick={handleSubmitDetails}
                                className="px-6 py-2.5 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors"
                            >
                                Submit Assessment
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FreeAssessment;
